import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Share2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  Beaker,
  Leaf,
  Droplets
} from 'lucide-react'

interface Recommendation {
  product_slug: string
  product_name: string
  category: string
  application_type: string
  rate: string
  timing: string
  rationale: string
  confidence: number
  talking_points: string[]
}

interface SectionTalkingPoint {
  text: string
  category: string
}

interface Section {
  section_key: string
  section_label: string
  application_type: string
  timing: string
  products: Recommendation[]
  notes: string | null
  talking_points: SectionTalkingPoint[]
}

interface CropResult {
  crop_type: string
  acres: number
  recommendations: Recommendation[]
  sections?: Section[]
  scoring_confidence: number
}

interface RecommendationData {
  session_id: string
  crops: CropResult[]
  scoring_confidence: number
}

const cropLabels: Record<string, string> = {
  corn: 'Corn',
  soybeans: 'Soybeans',
  wheat: 'Wheat',
  alfalfa: 'Alfalfa',
  potatoes: 'Potatoes',
  sugar_beets: 'Sugar Beets',
  dry_beans: 'Dry Beans',
}

const cropEmojis: Record<string, string> = {
  corn: '🌽',
  soybeans: '🫛',
  wheat: '🌾',
  alfalfa: '🌿',
  potatoes: '🥔',
  sugar_beets: '🫒',
  dry_beans: '🫘',
}

// Category icons
const categoryIcons: Record<string, React.ReactNode> = {
  'starter': <Sparkles className="w-5 h-5" />,
  'micronutrient': <Beaker className="w-5 h-5" />,
  'biological': <Leaf className="w-5 h-5" />,
  'foliar': <Droplets className="w-5 h-5" />,
}

export default function Results() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<RecommendationData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [activeCropIndex, setActiveCropIndex] = useState(0)

  useEffect(() => {
    async function fetchResults() {
      try {
        const res = await fetch(`/api/sessions/${sessionId}/recommendation`)
        if (!res.ok) throw new Error('Failed to fetch recommendations')
        const json = await res.json()
        setData(json)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    if (sessionId) {
      fetchResults()
    }
  }, [sessionId])

  const handleDownloadPDF = async () => {
    setDownloading(true)
    try {
      const res = await fetch(`/api/sessions/${sessionId}/pdf`)
      if (!res.ok) throw new Error('Failed to generate PDF')
      const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `recommendation-${sessionId}.pdf`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading PDF:', err)
    } finally {
      setDownloading(false)
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Product Recommendation',
          text: 'Check out this product recommendation from Yield Innovations',
          url: window.location.href,
        })
      } catch {
        // Share cancelled
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-yi-green spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading recommendations...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'No data found'}</p>
          <button
            onClick={() => navigate('/')}
            className="text-yi-green font-medium"
          >
            Return Home
          </button>
        </div>
      </div>
    )
  }

  const crops = data.crops || []
  const activeCrop = crops[activeCropIndex]

  // Use sections if available, otherwise fall back to grouping by application_type
  const sections = activeCrop?.sections
  const groupedRecs = !sections
    ? (activeCrop?.recommendations || []).reduce((acc, rec) => {
        const key = rec.application_type
        if (!acc[key]) acc[key] = []
        acc[key].push(rec)
        return acc
      }, {} as Record<string, Recommendation[]>)
    : null

  const slotLabels: Record<string, string> = {
    'in-furrow': 'In-Furrow',
    '2x2': '2x2',
    'sidedress': 'Sidedress',
    'foliar': 'Foliar',
    'hopper': 'Hopper Treatment',
    'adjuvant': 'Adjuvant',
  }

  const totalRecs = crops.reduce((sum, c) => sum + (c.recommendations?.length || 0), 0)

  return (
    <div className="min-h-dvh flex flex-col bg-gray-50 safe-top safe-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 text-gray-600 btn-press"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-medium text-gray-900">Recommendations</span>
          <button
            onClick={handleShare}
            className="p-2 -mr-2 text-gray-600 btn-press"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>

        {/* Crop tabs */}
        {crops.length > 1 && (
          <div className="flex gap-1 px-4 pb-3 overflow-x-auto">
            {crops.map((crop, idx) => (
              <button
                key={crop.crop_type}
                onClick={() => { setActiveCropIndex(idx); setExpandedProduct(null) }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
                  transition-colors
                  ${idx === activeCropIndex
                    ? 'bg-yi-green text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <span>{cropEmojis[crop.crop_type] || ''}</span>
                <span>{cropLabels[crop.crop_type] || crop.crop_type}</span>
                <span className="opacity-70">{crop.acres}ac</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Confidence Banner */}
      <div className="bg-yi-green text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80">Match Confidence</p>
            <p className="text-2xl font-bold">
              {Math.round((activeCrop?.scoring_confidence || data.scoring_confidence) * 100)}%
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-80">Products</p>
            <p className="text-2xl font-bold">
              {activeCrop?.recommendations?.length || 0}
            </p>
          </div>
          {crops.length > 1 && (
            <div className="text-right">
              <p className="text-sm opacity-80">Total (all crops)</p>
              <p className="text-2xl font-bold">{totalRecs}</p>
            </div>
          )}
        </div>
        {crops.length === 1 && activeCrop && (
          <p className="text-sm opacity-80 mt-1">
            {cropEmojis[activeCrop.crop_type]} {cropLabels[activeCrop.crop_type] || activeCrop.crop_type} &mdash; {activeCrop.acres} acres
          </p>
        )}
      </div>

      {/* Recommendations List */}
      <div className="flex-1 p-4 space-y-6">
        {sections ? (
          /* Section-based rendering */
          sections.map(section => (
            <div key={section.section_key}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1 px-2">
                {section.section_label}
              </h2>
              <p className="text-xs text-gray-400 mb-3 px-2">{section.timing}</p>

              {/* Section-level talking points (synergies) */}
              {section.talking_points.length > 0 && (
                <div className="mx-2 mb-3 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs font-semibold text-purple-700 mb-1">Consultant Notes</p>
                  {section.talking_points.map((tp, i) => (
                    <p key={i} className="text-xs text-purple-600 italic">{tp.text}</p>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                {section.products.map(rec => {
                  const cardKey = `${section.section_key}-${rec.product_slug}`
                  return (
                    <ProductCard
                      key={cardKey}
                      recommendation={rec}
                      expanded={expandedProduct === cardKey}
                      onToggle={() => setExpandedProduct(
                        expandedProduct === cardKey ? null : cardKey
                      )}
                    />
                  )
                })}
              </div>
            </div>
          ))
        ) : groupedRecs ? (
          /* Legacy flat grouping */
          Object.entries(groupedRecs).map(([slot, recs]) => (
            <div key={slot}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
                {slotLabels[slot] || slot}
              </h2>
              <div className="space-y-3">
                {recs.map(rec => (
                  <ProductCard
                    key={rec.product_slug}
                    recommendation={rec}
                    expanded={expandedProduct === rec.product_slug}
                    onToggle={() => setExpandedProduct(
                      expandedProduct === rec.product_slug ? null : rec.product_slug
                    )}
                  />
                ))}
              </div>
            </div>
          ))
        ) : null}

        {(activeCrop?.recommendations?.length || 0) === 0 && (
          <div className="text-center py-12 text-gray-500">
            No recommendations for this crop yet.
          </div>
        )}
      </div>

      {/* Download Button */}
      <div className="sticky bottom-0 pt-4 px-6 pb-6 bg-white border-t border-gray-100 safe-bottom">
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="w-full bg-yi-green text-white rounded-xl py-4 px-6 font-semibold
                     flex items-center justify-center gap-2 btn-press
                     disabled:opacity-50"
        >
          {downloading ? (
            <>
              <Loader2 className="w-5 h-5 spinner" />
              <span>Generating PDF...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Download Rec Sheet</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

function ProductCard({
  recommendation,
  expanded,
  onToggle
}: {
  recommendation: Recommendation
  expanded: boolean
  onToggle: () => void
}) {
  const confidenceColor = recommendation.confidence >= 0.7
    ? 'bg-green-100 text-green-800'
    : recommendation.confidence >= 0.4
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-gray-100 text-gray-800'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 text-left btn-press"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-yi-green/10 text-yi-green
                          flex items-center justify-center flex-shrink-0">
            {categoryIcons[recommendation.category] || <Beaker className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">
                {recommendation.product_name}
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${confidenceColor}`}>
                {Math.round(recommendation.confidence * 100)}%
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {recommendation.rate} · {recommendation.timing}
            </p>
          </div>
          <div className="text-gray-400 flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-50 animate-slide-up">
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Why this product?</p>
            <p className="text-sm text-gray-600">{recommendation.rationale}</p>
          </div>

          {recommendation.talking_points.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Talking Points</p>
              <ul className="space-y-2">
                {recommendation.talking_points.map((point, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-600">
                    <span className="text-yi-green flex-shrink-0">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
